import React, { useRef, useState, useMemo } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Users, Briefcase, X, Images } from "lucide-react";
import { FLEET, estimateFare } from "../data";
import { useTrip } from "../context/TripContext";

const Lightbox = ({ car, onClose }) => {
  const [active, setActive] = useState(0);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid={`lightbox-${car.id}`}
    >
      <button className="absolute top-6 right-6 text-white/60 hover:text-gold transition-colors duration-300" onClick={onClose} data-testid="lightbox-close">
        <X size={28} />
      </button>
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <p className="label text-gold mb-4">{car.name} — Interior</p>
        <div className="relative aspect-[16/10] overflow-hidden border border-white/10 bg-[var(--nl-bg-2)]">
          <motion.img key={active} initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} src={car.gallery[active]} alt={`${car.name} ${active}`} className="w-full h-full object-cover" />
        </div>
        <div className="flex gap-3 mt-4">
          {car.gallery.map((g, i) => (
            <button key={i} onClick={() => setActive(i)} data-testid={`lightbox-thumb-${i}`} className={`w-24 h-16 overflow-hidden border transition-colors duration-300 ${i === active ? "border-gold" : "border-white/10 opacity-60 hover:opacity-100"}`}>
              <img src={g} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const FleetCard = ({ car, index, onOpen }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const { trip } = useTrip();

  // Live estimate for this exact vehicle once the visitor has entered trip details.
  const est = useMemo(
    () => estimateFare({ km: trip.km, vehicle: car.name, mode: trip.mode, hours: trip.hours, deposit: false }),
    [trip, car.name]
  );
  const hasTrip = trip.mode === "hourly" || trip.km != null;

  const reserve = () => {
    try {
      sessionStorage.setItem("nl_prefill_vehicle", car.name);
      window.dispatchEvent(new CustomEvent("nl-prefill-vehicle", { detail: car.name }));
    } catch { /* ignore */ }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, delay: (index % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="group relative border border-white/10 bg-[var(--nl-bg-2)] overflow-hidden md:col-span-6"
      data-testid={`fleet-card-${car.id}`}
    >
      <div className="relative overflow-hidden h-[380px] md:h-[480px]">
        <motion.img
          style={{ y }}
          src={car.image}
          alt={car.name}
          className="w-full h-[120%] object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
        <span className="absolute top-6 left-6 label text-gold font-semibold bg-black/55 backdrop-blur-md px-3 py-1.5 border border-gold/30" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>{car.tagline}</span>
        <span className="absolute top-6 right-6 label text-white/70 bg-black/40 backdrop-blur-md border border-white/15 px-3 py-1.5" data-testid={`fleet-class-${car.id}`}>{car.class}</span>
        <button
          onClick={() => onOpen(car)}
          data-testid={`fleet-gallery-${car.id}`}
          className="absolute bottom-6 right-6 flex items-center gap-2 label bg-black/50 backdrop-blur-md border border-white/20 text-white px-4 py-2.5 hover:border-gold hover:text-gold transition-colors duration-300"
        >
          <Images size={15} /> View Interior
        </button>
      </div>

      <div className="p-8 md:p-10">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <h3 className="font-serif font-light text-3xl md:text-4xl tracking-tight">{car.name}</h3>
          <div className="flex items-center gap-6 text-white/60">
            <span className="flex items-center gap-2 text-sm"><Users size={16} className="text-gold" /> {car.seats}</span>
            <span className="flex items-center gap-2 text-sm"><Briefcase size={16} className="text-gold" /> {car.bags}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-6">
          {car.features.map((f) => (
            <span key={f} className="text-white/45 text-sm font-body">{f}</span>
          ))}
        </div>
        <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
          <p className="font-body text-white/80" data-testid={`fleet-price-${car.id}`}>
            {hasTrip && est ? (
              <>
                <span className="text-white/40 text-sm">Est. {trip.mode === "hourly" ? `${trip.hours}h` : "total"}</span>{" "}
                <span className="text-gold text-2xl font-serif">${est.total.toFixed(0)}</span>
                <span className="text-white/30 text-xs ml-1">incl. HST</span>
              </>
            ) : (
              <>
                <span className="text-white/40 text-sm">From</span>{" "}
                <span className="text-gold text-2xl font-serif">${car.priceFrom}</span>
              </>
            )}
          </p>
          <a
            href="#book"
            onClick={reserve}
            data-testid={`fleet-book-${car.id}`}
            className="inline-block label text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors duration-300"
          >
            Reserve this vehicle →
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export const Fleet = () => {
  const [lightbox, setLightbox] = useState(null);
  return (
    <section id="fleet" className="py-32 md:py-40 bg-[var(--nl-bg)]" data-testid="fleet">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <p className="label text-gold mb-6">The Fleet</p>
            <h2 className="font-serif font-light text-5xl md:text-7xl tracking-tighter leading-none">
              The fleet, <span className="italic text-gold">2026.</span>
            </h2>
          </div>
          <p className="text-white/50 max-w-sm font-body font-light">
            Latest-model sedans and SUVs — meticulously maintained, immaculately clean, and always chauffeur-driven across Ontario.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {FLEET.map((car, i) => (
            <FleetCard key={car.id} car={car} index={i} onOpen={setLightbox} />
          ))}
        </div>
      </div>
      <AnimatePresence>
        {lightbox && <Lightbox car={lightbox} onClose={() => setLightbox(null)} />}
      </AnimatePresence>
    </section>
  );
};
